import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import * as styles from "./FeaturePermissionsNew.module.scss";
import GridTable from "cvmgmt-cube-grid-table";
import { GridTableType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
import { LoadingSpinner } from "cprime-foundational-components";
import JustificationInput from "./JustificationInput";

const GRID_MULTISELECT_TYPE = "MULTI_SELECT";

function FeaturePermissionsNewTable({
  setFeatureTableRows,
  isLoading,
  featureTableRows,
  onJustficationChange,
  handleDeleteRow,
}: {
  setFeatureTableRows: any;
  isLoading: boolean;
  featureTableRows: any;
  onJustficationChange: any;
  handleDeleteRow: any;
}) {
  const scssClass = styles as any;

  const normalizedTableData = useMemo(() => {
    if (!featureTableRows?.columns) {
      return featureTableRows;
    }

    return {
      ...featureTableRows,
      // columns: featureTableRows.columns.filter(
      //   (column: any) =>
      //     column?.type !== "MULTISELECT" && column?.type !== "COMPONENT",
      // ),
      columns: featureTableRows.columns.map((column: any) =>
        column?.type === "MULTISELECT"
          ? { ...column, type: GRID_MULTISELECT_TYPE, size: 340 }
          : column?.type === "TOOLTIPTEXT"
            ? { ...column, size: 479 }
            : {
                ...column,
                size: column?.size ?? 200,
                minSize: column?.minSize ?? 200,
              },
      ),
    };
  }, [featureTableRows]);

  // State for "apply to all" checkbox functionality
  const [applyToAllChecked, setApplyToAllChecked] = useState(false);
  const applyToAllCheckedRef = useRef(applyToAllChecked);

  // Keep ref in sync with state
  useEffect(() => {
    applyToAllCheckedRef.current = applyToAllChecked;
  }, [applyToAllChecked]);

  const handleInputBlur = (
    data: any,
    value: string,
    fromElement: HTMLInputElement | null,
    toElement: HTMLElement | null,
    cursorPosition: number | null,
  ) => {
    // Early return if no data or required elements
    if (!data || !fromElement) return;

    // Check if values have diverged when checkbox is checked (auto-uncheck logic)
    if (applyToAllCheckedRef.current) {
      const firstRow = featureTableRows?.rows?.[0];
      const isFirstInput = firstRow?.feature_id === data?.feature_id;
      const currentFirstValue = firstRow?.inputtext || "";

      // Check if this input's value differs from the first row's original value
      // Compare the new value with what's currently in the first row
      if (value !== currentFirstValue) {
        // Values have diverged, uncheck the checkbox
        setApplyToAllChecked(false);
      }
    }

    // Capture element identifiers before DOM manipulation
    const fromFeatureId = fromElement.getAttribute("data-feature-id");
    const toFeatureId = toElement?.getAttribute("data-feature-id");

    /**
     * Simple focus strategy: Only restore focus if moving between input fields
     */
    const shouldRestoreFocus = () => {
      // Only restore focus if target is another input field with data-feature-id
      return toElement?.tagName === "INPUT" && toFeatureId;
    };

    /**
     * Updates the feature table state
     */
    const updateFeatureState = () => {
      const updatedObject = {
        ...data,
        inputtext: value,
      };

      setFeatureTableRows((prevData: any) => ({
        ...prevData,
        rows: prevData.rows.map((row: any) =>
          row.feature_id === updatedObject.feature_id ? updatedObject : row,
        ),
      }));
    };

    /**
     * Restores focus to the target input element
     */
    const restoreFocus = (targetFeatureId: string, preserveCursor: boolean) => {
      const inputToFocus = document.querySelector(
        `input[data-feature-id="${targetFeatureId}"]`,
      ) as HTMLInputElement | null;

      if (!inputToFocus) return;

      inputToFocus.focus();

      // Handle cursor positioning
      if (preserveCursor && cursorPosition !== null) {
        inputToFocus.setSelectionRange(cursorPosition, cursorPosition);
      } else {
        // For different inputs, allow natural cursor positioning
        requestAnimationFrame(() => {
          const currentPosition =
            inputToFocus?.selectionStart || inputToFocus?.value?.length;
          inputToFocus.setSelectionRange(currentPosition, currentPosition);
        });
      }
    };

    // Execute state update and focus restoration
    setTimeout(() => {
      updateFeatureState();

      requestAnimationFrame(() => {
        if (shouldRestoreFocus() && toFeatureId) {
          const preserveCursor = toFeatureId === fromFeatureId;
          restoreFocus(toFeatureId, preserveCursor);
        }
      });
    }, 100);
  };

  const handleMultiselectChange = (data: any) => {
    const checkViewPerm =
      data?.data?.length > 0 &&
      data?.data?.some((item: any) => item.value === "View");
    const updatedObject = {
      ...data.row,
      value: {
        ...data.row.value,
        selected: data.data,
        //error: !checkViewPerm,
      },
    };
    setFeatureTableRows((prevTdata: any) => ({
      ...prevTdata,
      rows: prevTdata.rows.map((row: any) =>
        row.feature_id === updatedObject.feature_id ? updatedObject : row,
      ),
    }));
  };

  // Handle changes to justification input
  const handleJustificationChange = useCallback(
    (value: string, featureId: string) => {
      // Call the original onChange handler
      onJustficationChange?.(value, featureId);
    },
    [onJustficationChange],
  );

  // Handle "apply to all" checkbox change
  const handleApplyToAllChange = useCallback(
    (checked: boolean) => {
      setApplyToAllChecked(checked);

      if (checked) {
        const currentFirstValue = featureTableRows?.rows?.[0]?.inputtext || "";
        if (currentFirstValue) {
          // Apply the first justification to all rows
          setFeatureTableRows((prevData: any) => ({
            ...prevData,
            rows: prevData.rows.map((row: any) => ({
              ...row,
              inputtext: currentFirstValue,
            })),
          }));
        }
      }
    },
    [featureTableRows, setFeatureTableRows],
  );

  const JustificationInputWrapper = useCallback(
    ({ row, ...props }: any) => {
      const firstRow = featureTableRows?.rows?.[0];
      const isFirst = firstRow?.feature_id === row?.feature_id;
      const currentFirstValue = firstRow?.inputtext || "";

      return (
        <JustificationInput
          {...props}
          row={row}
          onBlur={handleInputBlur}
          onChange={handleJustificationChange}
          isFirst={isFirst}
          applyToAllChecked={applyToAllCheckedRef.current}
          onApplyToAllChange={handleApplyToAllChange}
          firstJustificationValue={currentFirstValue}
          totalRows={featureTableRows?.rows?.length || 0}
        />
      );
    },
    [
      featureTableRows,
      handleJustificationChange,
      handleApplyToAllChange,
      handleInputBlur,
    ],
  );

  const staticTableProps: GridTableType = useMemo(
    () => ({
      title: "Permissions",
      data: normalizedTableData,
      isRowSelection: false,
      isSorting: false,
      isFiltering: false,
      cellActions: [
        {
          event: "Delete",
          callback: (event, data) => {
            handleDeleteRow(event, data);
          },
          icon: true,
        },
        {
          event: "COMPONENT",
          component: <JustificationInputWrapper />,
        },
        {
          event: GRID_MULTISELECT_TYPE,
          callback: (data) => handleMultiselectChange(data),
        },
      ],
      showLoader: false,
      loading: isLoading,
      overscan: 250,
    }),
    [
      normalizedTableData,
      isLoading,
      JustificationInputWrapper,
      handleDeleteRow,
    ],
  );

  return (
    <div className={scssClass.tableContainer}>
      {isLoading ? (
        <LoadingSpinner fullScreen={true} />
      ) : (
        <GridTable
          style={{ height: "calc(100vh - 26.5rem)" }}
          {...staticTableProps}
        />
      )}
    </div>
  );
}

export default React.memo(
  FeaturePermissionsNewTable,
  (prevProps, nextProps) => {
    const isLoadingUnchanged = prevProps.isLoading === nextProps.isLoading;
    const featureTableRowsUnchanged =
      prevProps.featureTableRows === nextProps.featureTableRows;

    // Re-render only if both values are unchanged
    return isLoadingUnchanged && featureTableRowsUnchanged;
  },
);
